import { useMelwaysLink } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Columns,
  Column,
  Stack,
  PageBlock,
  ContentBlock,
  Divider,
  useResponsiveValue,
  HiddenVisually,
} from 'braid-design-system';
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';

import { AvatarContainer } from 'src/components/Profile/Avatar/AvatarContainer/index.ts';
import {
  PERSONAL_DETAILS,
  SUMMARY,
  CAREER_HISTORY,
  RESUME,
  NEXT_ROLE,
  SKILLS,
  EDUCATION,
  TIPS,
  LICENCES,
  LANGUAGES,
  PROFILE_VISIBILITY,
  PROFILE_VISIBILITY_V2,
  PROFILE_INSIGHTS,
  VERIFICATIONS,
  REFERENCES,
  PEOPLE_SEARCH,
} from 'src/components/Router';
import { CardRoute } from 'src/components/Router/CardRoute';
import { useCard } from 'src/components/Router/useCard';
import { usePage } from 'src/components/Router/usePage';
import { analyticsClient } from 'src/modules/analytics/index.ts';
import { useAppConfig } from 'src/modules/appConfig';
import { hotjarEvent } from 'src/modules/hotjar';

import { myProfileRendered } from '../../middlewares/analytics/profile/profile.ts';
import { usePeopleSearchEnabled } from '../../modules/features/usePeopleSearchEnabled.ts';

import translations from './.vocab';
import { useGetProfileAvatar } from './Avatar/data.ts';
import { DefaultResumeNudge } from './DefaultResumeNudge/DefaultResumeNudge';
import { usePreferredLocations } from './NextRole/PreferredLocation/data';
import { useRightsToWork } from './NextRole/RightToWork/data';
import type { ReadPersonalDetailsProps } from './PersonalDetails/ReadPersonalDetails/ReadPersonalDetails';
import { usePersonalDetails, usePublicProfile } from './PersonalDetails/data';
import { useGetProfileInsights } from './ProfileInsights/data';
import { useReferenceChecks } from './References/data';
import { useResumes } from './Resume/data';
import { TopNudge } from './TopNudge/TopNudge';
import { useVerifications } from './Verifications/data';
import { isPreferredLocationDeprecated, isRTWCoarse } from './utils';

import * as styles from './Profile.css';

const sectionSpace = 'xxlarge';
const rightColumnSectionSpace = 'large';

export const Profile = React.memo(() => {
  const { t } = useTranslations(translations);

  const { brand } = useAppConfig();
  const { resumes, defaultResumeId } = useResumes();
  // Call hooks at Profile level - stays mounted during resize
  const {
    personalDetails,
    loading: personalDetailsLoading,
    error: personalDetailsError,
  } = usePersonalDetails();
  const {
    data: publicProfileData,
    loading: publicProfileLoading,
    error: publicProfileError,
  } = usePublicProfile();
  const { preferredLocations } = usePreferredLocations();
  const { rightsToWork } = useRightsToWork();
  const { showVerifications } = useVerifications();
  const { referenceChecks } = useReferenceChecks();
  const {
    profileAvatar,
    loading: profileAvatarLoading,
    error: profileAvatarError,
  } = useGetProfileAvatar();
  const isPeopleSearchEnabled = usePeopleSearchEnabled();
  const dispatch = useDispatch();
  const currentPage = usePage();
  const currentCard = useCard();

  const isMobileOrTablet = useResponsiveValue()({
    mobile: true,
    tablet: true,
    desktop: false,
  });

  const { showProfileInsights } = useGetProfileInsights();

  const firstName = personalDetails?.personalDetails?.firstName || '';

  useEffect(() => {
    if (showProfileInsights) {
      hotjarEvent('profile-insights-test-group-show');
    }
  }, [showProfileInsights]);

  useEffect(() => {
    if (!personalDetailsLoading) {
      dispatch(myProfileRendered());
      analyticsClient.pageUpdated({ currentPage });
    }
  }, [dispatch, personalDetailsLoading, currentPage]);

  const showReferences = referenceChecks && referenceChecks.length > 0; // Remove this condition to unhide empty References card

  // Add support for profile-visibility route
  const profileVisibilityCard =
    currentCard === PROFILE_VISIBILITY
      ? PROFILE_VISIBILITY
      : PROFILE_VISIBILITY_V2;

  // Props to pass to PersonalDetails component via CardRoute
  const personalDetailsProps = {
    personalDetails,
    personalDetailsLoading,
    isPersonalDetailsError: Boolean(personalDetailsError),
    publicProfileData,
    publicProfileLoading,
    isPublicProfileError: Boolean(publicProfileError),
  } satisfies ReadPersonalDetailsProps;

  const rightColumnContents = (
    <>
      <Stack space={rightColumnSectionSpace}>
        {isMobileOrTablet ? (
          <>
            <CardRoute
              card={PERSONAL_DETAILS}
              componentProps={personalDetailsProps}
            />
            <Divider />
          </>
        ) : null}
        <CardRoute card={profileVisibilityCard} />
        {showProfileInsights ? (
          <>
            <Divider />
            <CardRoute card={PROFILE_INSIGHTS} />
          </>
        ) : null}
        {showVerifications ? (
          <>
            <Divider />
            <CardRoute card={VERIFICATIONS} />
          </>
        ) : null}
      </Stack>
      <>
        <Box paddingTop={rightColumnSectionSpace} paddingBottom={sectionSpace}>
          <Divider />
        </Box>
        <Box className={styles.sticky}>
          <Stack space="xlarge">
            <CardRoute card={TIPS} />
            {!personalDetailsLoading && isPeopleSearchEnabled && (
              <CardRoute card={PEOPLE_SEARCH} />
            )}
          </Stack>
        </Box>
      </>
    </>
  );

  const profileUrl = useMelwaysLink({ path: '/profile', absolute: true });

  const showPriorityNudge = () => {
    if (personalDetails?.currentLocation2?.isDeprecated) {
      return <TopNudge nudgeType="HOME_LOCATION" />;
    } else if (resumes.length > 0 && defaultResumeId === '') {
      return <DefaultResumeNudge />;
    } else if (
      rightsToWork &&
      rightsToWork.length > 0 &&
      isRTWCoarse(rightsToWork)
    ) {
      return <TopNudge nudgeType="RTW" />;
    } else if (
      preferredLocations &&
      preferredLocations.length > 0 &&
      isPreferredLocationDeprecated(preferredLocations)
    ) {
      return <TopNudge nudgeType="PREFERRED_LOCATIONS" />;
    }
    return null;
  };

  const renderBannerContent = () => (
    <Box
      className={styles.avatarBanner}
      data={{ automation: 'profile-avatar-container' }}
    >
      <AvatarContainer
        avatarUrls={profileAvatar}
        loading={profileAvatarLoading}
        imageError={profileAvatarError}
      />
    </Box>
  );

  return (
    <>
      <Helmet>
        <title>{t('My Profile | brand', { brand })}</title>
        <link rel="canonical" href={profileUrl} />
      </Helmet>
      {!firstName && (
        <HiddenVisually>
          <h1>{t('Brand profile', { brand })}</h1>
        </HiddenVisually>
      )}
      <Box paddingX={{ tablet: 'gutter' }}>
        <Stack space="xlarge">
          <ContentBlock width="large">
            <Box
              background="brand"
              borderRadius={{ tablet: 'xlarge' }}
              className={styles.bannerWithAvatar}
            >
              <PageBlock width="medium">{renderBannerContent()}</PageBlock>
            </Box>
          </ContentBlock>

          <PageBlock width="medium">
            <Columns space={sectionSpace} collapseBelow="desktop" reverse>
              <Column width="content">
                <Box className={styles.rightColumn}>{rightColumnContents}</Box>
              </Column>
              <Column>
                <Stack space={sectionSpace}>
                  {!isMobileOrTablet ? (
                    <CardRoute
                      card={PERSONAL_DETAILS}
                      componentProps={personalDetailsProps}
                    />
                  ) : null}
                  {showPriorityNudge()}
                  <CardRoute card={SUMMARY} />
                  <CardRoute card={CAREER_HISTORY} />
                  <CardRoute card={EDUCATION} />
                  <CardRoute card={LICENCES} />
                  <CardRoute card={SKILLS} />
                  <CardRoute card={LANGUAGES} />
                  <CardRoute card={RESUME} />
                  {showReferences && <CardRoute card={REFERENCES} />}
                  <CardRoute card={NEXT_ROLE} />
                </Stack>
              </Column>
            </Columns>
          </PageBlock>
        </Stack>
      </Box>
    </>
  );
});
